import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  Dialog,
  Divider,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { createApiKey, listApiKeys, revokeApiKey } from '../../api/apiKeys';
import { extractErrorMessage } from '../../api/extractErrorMessage';
import {
  getPermissionsByCategory,
  hasPermission,
  BLOCKED_API_KEY_SCOPES,
} from '../../constants/permissions';
import usePermissions from '../../hooks/usePermissions';
import DWLFLoader from '../../components/DWLFLoader';

// Keys can carry 30+ granular scopes. Showing them all made the Scopes cell
// unbounded and pushed Actions (revoke) off the right of the viewport — the
// button was there all along, just unreachable without horizontal scrolling.
const MAX_VISIBLE_SCOPES = 3;

/**
 * Split a key's scopes into the chips to render and how many are hidden.
 * Exported so the bounding logic is testable without mounting the whole tab.
 */
export const splitScopesForDisplay = (scopes, max = MAX_VISIBLE_SCOPES) => {
  const list = Array.isArray(scopes) ? scopes : [];
  const shown = list.slice(0, max);
  // `hidden` is returned rather than re-derived at the call site: the tooltip
  // must show the COMPLEMENT of the chips, and a second `.slice()` in the render
  // path would be logic the tests do not actually cover.
  const hidden = list.slice(max);
  return { shown, hidden, hiddenCount: hidden.length };
};

/** The Scopes cell contents. Split out so the shipped render path is testable. */
export const ScopeChips = ({ scopes }) => {
  const { shown, hidden, hiddenCount } = splitScopesForDisplay(scopes);
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: 260 }}>
      {shown.map((scope, i) => (
        <Chip
          // scope+index: a duplicate scope inside the visible slice would
          // otherwise collide on the React key.
          key={`${scope}-${i}`}
          label={scope}
          size="small"
          variant="outlined"
          color={scope === 'write' ? 'warning' : 'default'}
        />
      ))}
      {hiddenCount > 0 && (
        <Tooltip title={hidden.join(', ')}>
          {/* tabIndex + aria-label: a Tooltip on a Chip is hover-only by default,
              so the hidden scopes were unreachable by keyboard. Focusable makes
              the tooltip open on focus, and the label carries the same content
              for screen readers and touch, which get no hover at all. */}
          <Chip
            label={`+${hiddenCount} more`}
            size="small"
            variant="outlined"
            color="default"
            tabIndex={0}
            aria-label={`${hiddenCount} more scopes: ${hidden.join(', ')}`}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

const EXPIRY_OPTIONS = [
  { label: 'No expiry', value: '' },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '180 days', value: 180 },
  { label: '365 days', value: 365 },
];

const formatDate = (dateString) => {
  if (!dateString) {return 'Never';}
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ApiKeysTab = () => {
  // Key list state
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Generate form state
  const [generateOpen, setGenerateOpen] = useState(false);
  const [name, setName] = useState('');
  // 'legacy' = single 'read' scope. 'granular' = pick specific permissions.
  // The legacy 'write' option was removed because it implicitly grants
  // markets:candles, which the backend rejects for external API keys due
  // to Twelve Data licensing restrictions on raw OHLCV redistribution.
  // Users who need write access must use granular mode.
  const [scopeMode, setScopeMode] = useState('legacy');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  // Granular scope selection state
  const [selectedScopes, setSelectedScopes] = useState(new Set());

  // Use cached Redux permissions instead of ad-hoc getMe() calls
  const { permissions: userPermissions, loading: permissionsLoading, error: permissionsError } = usePermissions();

  // Raw key display dialog
  const [rawKeyData, setRawKeyData] = useState(null);
  const [copied, setCopied] = useState(false);

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listApiKeys();
      setKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load API keys.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  // --- Generate key ---
  const handleOpenGenerate = () => {
    setName('');
    setScopeMode('legacy');
    setSelectedScopes(new Set());
    setExpiresInDays('');
    setGenerateError(null);
    setGenerateOpen(true);
  };

  const handleScopeToggle = (scopeKey) => {
    setSelectedScopes(prev => {
      const next = new Set(prev);
      next.has(scopeKey) ? next.delete(scopeKey) : next.add(scopeKey);
      return next;
    });
  };

  const handleGenerate = async () => {
    if (!name.trim()) {
      setGenerateError('Name is required.');
      return;
    }
    try {
      setGenerating(true);
      setGenerateError(null);
      
      let scopes;
      if (scopeMode === 'granular') {
        if (selectedScopes.size === 0) {
          setGenerateError('Select at least one permission in granular mode.');
          setGenerating(false);
          return;
        }
        scopes = Array.from(selectedScopes);
      } else {
        // Legacy mode is read-only by design — see comment on scopeMode
        // state above. Anything broader has to use granular mode.
        scopes = ['read'];
      }
      
      const result = await createApiKey({
        name: name.trim(),
        scopes,
        expiresInDays: expiresInDays || undefined,
      });
      setGenerateOpen(false);
      setRawKeyData(result);
      setCopied(false);
      fetchKeys();
    } catch (err) {
      setGenerateError(extractErrorMessage(err, 'Failed to generate API key.'));
    } finally {
      setGenerating(false);
    }
  };

  // --- Copy to clipboard ---
  const handleCopy = async () => {
    if (rawKeyData?.rawKey) {
      try {
        await navigator.clipboard.writeText(rawKeyData.rawKey);
        setCopied(true);
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = rawKeyData.rawKey;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
      }
    }
  };

  // --- Revoke key ---
  const handleConfirmRevoke = async () => {
    if (!revokeTarget) {return;}
    try {
      setRevoking(true);
      await revokeApiKey(revokeTarget.keyId);
      setRevokeTarget(null);
      fetchKeys();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to revoke key.'));
      setRevokeTarget(null);
    } finally {
      setRevoking(false);
    }
  };

  // --- Render ---
  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              API Keys
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage API keys for AI assistants and external tool access.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenGenerate}
          >
            Generate Key
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <DWLFLoader size="medium" />
          </Box>
        ) : keys.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={6}
            sx={{ textAlign: 'center' }}
          >
            <VpnKeyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420 }}>
              API keys let AI assistants and external tools access your DWLF account.
              Generate a key to get started.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Scopes</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((key) => (
                  <TableRow key={key.keyId} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <KeyIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight={500}>
                          {key.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                      >
                        {key.keyPrefix}••••••
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/*
                        Rendering every scope as a chip made this cell unbounded —
                        a 28-scope key pushed the Actions column (and its revoke
                        button) off-screen, so the control looked absent.
                        NB the width cap lives on the Stack inside ScopeChips, NOT
                        on this TableCell: max-width does not apply to table-cell
                        elements under the automatic table layout MUI uses, so it
                        would be a no-op here. The chip cap is the real bound.
                      */}
                      <ScopeChips scopes={key.scopes} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(key.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(key.lastUsedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(key.expiresAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke key">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setRevokeTarget(key)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Generate Key Dialog */}
      <Dialog
        open={generateOpen}
        onClose={() => !generating && setGenerateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Key Name"
              placeholder="e.g. Claude Agent, Trading Bot"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              autoFocus
              disabled={generating}
            />

            <Box>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Scope Mode
              </Typography>
              <RadioGroup
                row
                value={scopeMode}
                onChange={(e) => setScopeMode(e.target.value)}
              >
                <FormControlLabel
                  value="legacy"
                  control={<Radio size="small" />}
                  label="Legacy (read-only)"
                  disabled={generating}
                />
                <FormControlLabel
                  value="granular"
                  control={<Radio size="small" />}
                  label="Granular"
                  disabled={generating}
                />
              </RadioGroup>
            </Box>

            {scopeMode === 'legacy' ? (
              <Box>
                <Typography variant="body2" fontWeight={500} gutterBottom>
                  Permissions
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked disabled />}
                  label="Read"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Legacy mode creates a read-only key. For write access or
                  to grant specific permissions, switch to Granular mode.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Select permissions for this key. You can only grant permissions you already have.
                </Typography>
                {/* Deliberately generic: raw candles USED to be named here, but the
                    platform owner may now grant markets:candles on their own keys, so
                    naming it would contradict the checkbox shown to them. */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Some permissions cannot be granted to external API keys due to data licensing.
                </Typography>
                {permissionsError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    Failed to load your permissions. Please close and try again.
                  </Alert>
                )}
                {permissionsLoading && (
                  <Typography variant="body2" color="text.secondary">Loading permissions…</Typography>
                )}
                {Object.entries(getPermissionsByCategory()).map(([category, permissions]) => {
                  // Show only permissions the user actually has AND that
                  // are eligible to be granted to external API keys.
                  // BLOCKED_API_KEY_SCOPES is the frontend mirror of the
                  // backend's reject list — see permissions.js for the
                  // licensing rationale.
                  const available = permissions.filter(
                    p => hasPermission(userPermissions, p.key) && !BLOCKED_API_KEY_SCOPES.has(p.key),
                  );
                  if (available.length === 0) {return null;}
                  return (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'capitalize', mb: 0.5 }}>
                        {category}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0}>
                        {available.map(perm => (
                          <FormControlLabel
                            key={perm.key}
                            control={
                              <Checkbox
                                checked={selectedScopes.has(perm.key)}
                                onChange={() => handleScopeToggle(perm.key)}
                                size="small"
                                disabled={generating}
                              />
                            }
                            label={
                              <Tooltip title={perm.description || ''}>
                                <Typography variant="body2">{perm.key}</Typography>
                              </Tooltip>
                            }
                            sx={{ minWidth: '200px' }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Expiry</InputLabel>
              <Select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                label="Expiry"
                disabled={generating}
              >
                {EXPIRY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.label} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {generateError && (
              <Alert severity="error">{generateError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={generating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating || !name.trim()}
            startIcon={generating ? <DWLFLoader size="tiny" /> : <KeyIcon />}
          >
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Raw Key Display Dialog */}
      <Dialog
        open={Boolean(rawKeyData)}
        onClose={() => setRawKeyData(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key Generated</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This key will only be shown once. Copy it now!
          </Alert>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            {rawKeyData?.name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}
            >
              {rawKeyData?.rawKey}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {copied && (
            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
              Copied to clipboard ✓
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setRawKeyData(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={Boolean(revokeTarget)}
        onClose={() => !revoking && setRevokeTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke <strong>{revokeTarget?.name}</strong>?
            Any tools or agents using this key will immediately lose access. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRevoke}
            disabled={revoking}
          >
            {revoking ? 'Revoking…' : 'Revoke Key'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApiKeysTab;
